import { customElement, property, state } from "lit/decorators.js";
import { css, html, LitElement } from "lit";
import { provide } from "@lit/context";
import { filterContext, IFilter, IFilterContext, IFilterResult } from "./filterContext";
import { replaceElementWithHtml } from "../../util/dom";

@customElement("ehm-filter")
export class Filter extends LitElement {

  static styles = css`
    :host {
      display: block;
      margin-bottom: 6rem;
    }
    @media (max-width: 450px) {
      :host {
        margin-bottom: 4rem;
      }
    }
  `;

  private _fetchAbort?: AbortController;

  @property({ type: String, attribute: "endpoint" })
  endpoint = "/api/search";

  @property({ type: Number, attribute: "total" })
  total = 0;

  @property({ type: Number, attribute: "page-size" })
  pageSize = 0;

  @property({ type: Boolean, attribute: "retain-filters" })
  retainFilters = false;

  @state()
  @provide({ context: filterContext })
  private _filterContext: IFilterContext = {
    filters: [],
    updateFilters: this.handleFilterChange.bind(this),
    resetFacets: this.handleResetFacets.bind(this),
    isLoading: false,
    total: this.total,
    pageSize: this.pageSize,
    abortFetch: this.abortFetch.bind(this)
  };

  connectedCallback(): void {
    super.connectedCallback();
    // history.scrollRestoration = "manual"
    this.addEventListener("submit", this.handleFormSubmit);
    this.addEventListener("reset", this.handleFormReset);
    this.addEventListener("pagination-changed", this.handlePagination);
    this.addEventListener("tab-changed", this.handleTabChange);

    window.addEventListener("popstate", this.handlePagination);
    this._filterContext = {
      ...this._filterContext,
      filters: this.getActiveFilters(),
      total: this.total,
      pageSize: this.pageSize,
      currentForm: this.querySelector("form")
    };
  }

  disconnectedCallback(): void {
    // history.scrollRestoration = "auto";
    this.removeEventListener("submit", this.handleFormSubmit);
    this.removeEventListener("pagination-changed", this.handlePagination);
    this.removeEventListener("tab-changed", this.handleTabChange as EventListener);
    window.removeEventListener("popstate", this.handlePagination);
    super.disconnectedCallback();
  }

  private async handleFormReset(e: Event) {

    e.preventDefault();
    const url = new URL(window.location.href);

    const input = this.querySelector("input[name=\"q\"]") as HTMLInputElement;

    if (input) {
      input.value = "";
      input.focus();
    }
    url.searchParams.delete("q");

    url.searchParams.delete("first");
    url.searchParams.delete("id");
    window.history.replaceState(null, "", url);

    await this.updateResult(url.searchParams);
  }

  private async handleResetFacets() {
    const url = new URL(window.location.href);

    const input = this.querySelector("input[name=\"q\"]") as HTMLInputElement;

    const searchParams = new URLSearchParams();
    if (input?.value) {
      searchParams.set("q", input.value);
    }
    const sort = url.searchParams.get("sort");
    if (sort) {
      searchParams.set("sort", sort);
    }
    this.querySelectorAll("ehm-filter-tabs").forEach(tabEl => {
      const param = tabEl.getAttribute("param");
      if (param) {
        const value = url.searchParams.get(param);
        if (value) searchParams.set(param, value);
      }
    });
    url.search = searchParams.toString();

    url.searchParams.delete("first");
    url.searchParams.delete("id");
    window.history.replaceState(null, "", url);
    
    await this.updateResult(url.searchParams);
  }

  private async handleFormSubmit(e: SubmitEvent) {
    e.preventDefault();
    this.dispatchEvent(new CustomEvent("search-executed", { detail: e, bubbles: true, composed: true }));

    const url = new URL(window.location.href);
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form, e.submitter);
    const newQuery = formData.get("q") as string;
    const submitter = e.submitter as HTMLButtonElement;

    if ((!this.retainFilters && url.searchParams.get("q") !== newQuery) || submitter?.value == "clear-facets") {
      //Clear previous params if new search
      const newSearchParams = new URLSearchParams();
      if (newQuery) {
        newSearchParams.set("q", newQuery);
      }
      url.search = newSearchParams.toString();
    } else {
      url.searchParams.delete("p");
      url.searchParams.delete("first");
      url.searchParams.delete("id");
      if (newQuery) {
        url.searchParams.set("q", newQuery);
      } else {
        url.searchParams.delete("q");
      }
      formData.forEach((value, key) => {
        if (key === "q") return;
        if (!url.searchParams.getAll(key).includes(value as string)) {
          url.searchParams.append(key, value as string);
        }
      });
      if (submitter) {
        url.searchParams.set(submitter.name, submitter.value);
      }
    }

    window.history.replaceState(null, "", url);

    await this.updateResult(url.searchParams);
  }

  private async handleFilterChange({ param, value }: IFilter, operation: "add" | "remove") {
    const url = new URL(window.location.href);
    if (operation === "add") {
      url.searchParams.append(param, value);
    } else {
      url.searchParams.delete(param, value);
    }

    url.searchParams.delete("p");
    url.searchParams.delete("first");
    url.searchParams.delete("id");

    window.history.replaceState(null, "", url);

    await this.updateResult(url.searchParams);
  }

  private handleTabChange = async (e: Event) => {
    const { param, value } = (e as CustomEvent).detail;
    const url = new URL(window.location.href);

    const newParams = new URLSearchParams();
    const q = url.searchParams.get("q");
    const sort = url.searchParams.get("sort");
    if (q) newParams.set("q", q);
    if (sort) newParams.set("sort", sort);
    newParams.set(param, value);

    url.search = newParams.toString();
    window.history.replaceState(null, "", url);

    await this.updateResult(url.searchParams);
  };

  private updateResult = async (searchParams: URLSearchParams) => {

    this._filterContext = {
      ...this._filterContext,
      filters: this.getActiveFilters(),
      isLoading: true
    };

    try {
      const data = await this.fetchResult(searchParams);
      this.handleResultAndUpdateDom(data);
    } catch (err) {
      if (!this.isAbortError(err)) {
        console.error(err);
      }
    } finally {
      this.setLoading(false);
    }
  };

  private handleResultAndUpdateDom(data: IFilterResult) {
    this.updateDOM(data.pagination, ".pagination-partial", true);
    this.updateDOM(data.results, "ehm-filter-results", false);
    this._filterContext = {
      ...this._filterContext,
      total: data.total,
      pageSize: data.pageSize,
      result: data
    };
  }

  private handlePagination = async (e: any) => {
    e.preventDefault();

    const newUrl = new URL(window.location.href);
    if (e.detail?.page) {
      newUrl.searchParams.set("p", e.detail.page);
    }

    this.setLoading(true);
    //Start fetching but wait until scrolling is complete to resolve
    const fetchPromise = this.fetchResult(newUrl.searchParams);
    await this.scrollToResults();
    if (e.detail?.page) {
      window.history.pushState(null, "", newUrl);
    }
    try {
      const data = await fetchPromise;
      this.updateDOM(data.pagination, ".pagination-partial", true);
      this.updateDOM(data.results, "ehm-filter-results", false);
    } catch (err) {
      if (!this.isAbortError(err)) {
        console.error(err);
      }
    } finally {
      this.setLoading(false);
    }
  };

  private getActiveFilters(): IFilter[] {
    const searchParams = new URLSearchParams(window.location.search);
    const filters: IFilter[] = [];
    const inputs = Array.from(this.querySelectorAll<HTMLInputElement>("input"));
    searchParams.forEach((value, key) => {
      if (key === "q") return;
      for (const el of inputs) {
        if (el.name === key && el.value === value) {
          filters.push({ param: key, value, name: el.dataset.label || value });
        }
      }
    });
    return filters;
  }

  private updateDOM(html: string, selector: string, replace: boolean = false) {
    const targetElement = this.querySelector(selector);
    if (targetElement) {
      replaceElementWithHtml(html, targetElement, replace);
    }
  }

  private scrollToResults(): Promise<void> {
    const results = this.querySelector("ehm-filter-results");
    if (!results) return Promise.resolve();

    return new Promise<void>((resolve) => {
      const offset = 150;
      const top = Math.max(results.getBoundingClientRect().top + window.scrollY - offset, 0);
      if (Math.abs(top - window.scrollY) < 100) {
        resolve();
        return;
      }

      const supportsScrollEnd = "onscrollend" in window;

      if (supportsScrollEnd) {
        const onEnd = () => {
          window.removeEventListener("scrollend", onEnd as EventListener);
          resolve();
        };
        window.addEventListener("scrollend", onEnd as EventListener, { once: true, passive: true });
      } else {
        setTimeout(() => resolve(), 500);
      }

      setTimeout(() => {
        window.scrollTo({ top, behavior: "smooth" });
      });
    });
  }

  async fetchResult(searchParams: URLSearchParams) {
    this._fetchAbort?.abort();
    this._fetchAbort = new AbortController();

    // const url = new URL(window.location.href);
    const response = await fetch(`${this.endpoint}?${searchParams.toString()}`, {
      method: "GET",
      signal: this._fetchAbort.signal,
      headers: { "Accept": "application/json" }
    });

    if (!response.ok) throw new Error(`Search failed: ${response.status}`);

    return await response.json() as IFilterResult;

  }

  private abortFetch() {
    this._fetchAbort?.abort();
    this.setLoading(false);
  }

  private isAbortError(err: unknown): boolean {
    // Some browsers throw DOMException with name 'AbortError'; guard for environments without DOMException
    try {
      return err instanceof DOMException ? err.name === "AbortError" : (typeof err === "object" && err !== null && "name" in err && (err as any).name === "AbortError");
    } catch {
      return false;
    }
  }

  setLoading(isLoading: boolean) {
    this._filterContext = {
      ...this._filterContext,
      isLoading
    };
  }

  protected render() {
    return html`
      <slot></slot>`;
  }
}
